import * as React from "react";
import {Component} from "react";
import Card from "../../../elements/Card";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import {Checkbox, Control, Field, Icon, TextArea} from "bloomer";
import Button from "../../../elements/Button";
import {getTextToSpeechResult} from "../../../api/models/TextToSpeech";
import TextToSpeechConfig from "../../../components/model/TextToSpeech/TextToSpeechConfig";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import SectionName from "../../../elements/SectionName";
import FieldDescription from "../../../elements/FieldDescription";
import Spacer from "../../../elements/Spacer";
import {
    AudioContainer,
    AutoPlayLabel,
    CharactersCountLabel,
    DownloadButtonContainer,
    ErrorMessage,
    InformationCard,
    InputControl,
    InputLabel,
    InputRangeValue,
    InputWarning,
    RangeInput,
    SelectField,
    SelectInputControl,
    SelectOption,
    TextInputWarning,
    TextInputWarningsContainer
} from "../../../components/model/TextToSpeech/TextToSpeechStyledComponents";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

interface ITextToSpeechModelState {
    loading: boolean;
    config: any;
    text: string;
    textEmpty: boolean;
    charactersCount: number;
    showLimitWarning: boolean;
    languageCode: string;
    gender: string;
    genderInvalid: boolean;
    voiceNameOptions: any;
    voiceName: string;
    voiceNameInvalid: boolean;
    speed: number;
    pitch: number;
    result: any;
    autoPlay: boolean;
    errorMessage: string;
}

class TextToSpeechModel extends Component<{}, ITextToSpeechModelState> {
    public emptyOption: any = {
        id: "",
        name: "-"
    };
    public languageCodeOptions: any[] = [];
    public genderOptions: any[] = [this.emptyOption, {
        id: "male",
        name: "Male"
    }, {
        id: "female",
        name: "Female"
    }];
    public CHARACTERS_LIMIT = 5000;
    public audioStyle = {width: '100%'};

    public constructor(props: {}) {
        super(props);

        this.languageCodeOptions = [];

        this.state = {
            loading: false,
            config: {},
            text: "",
            textEmpty: false,
            charactersCount: 0,
            showLimitWarning: false,
            languageCode: "en-US",
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false,
            speed: 1,
            pitch: 0,
            result: null,
            autoPlay: false,
            errorMessage: ""
        };
    }

    public updateCharactersCount = async () => {
        const charactersCount = this.state.text.trim().length;
        this.setState({
            charactersCount,
            showLimitWarning: charactersCount > this.CHARACTERS_LIMIT
        });
    };

    public async componentDidMount() {
        const config = await TextToSpeechConfig.load();
        if (config) {
            this.setState({
                config
            });
            this.languageCodeOptions = Object.keys(config).map((c) => {
                return {
                    code: c,
                    name: config[c].name
                };
            }).sort((a: any, b: any) => {
                return a.name > b.name ? 1 : -1;
            });
        }
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TEXT_TO_SPEECH);
    }

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            text: "",
            textEmpty: false,
            charactersCount: 0,
            showLimitWarning: false,
            languageCode: "en-US",
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false,
            speed: 1,
            pitch: 0,
            result: null,
            autoPlay: false,
            errorMessage: ""
        });
    };

    public handleTextChange = (e: any) => {
        this.setState({
            text: e.target.value,
            textEmpty: false,
            errorMessage: ""
        }, async () => {
            await this.updateCharactersCount();
        });
    };

    public handleSelectLanguageChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageCode: event.currentTarget.value,
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false
        });
    };

    public handleSelectGenderChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            gender: event.currentTarget.value,
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false
        }, () => {
            if (this.state.languageCode) {
                const config = this.state.config[this.state.languageCode];
                if (config) {
                    if (this.state.gender) {
                        const voiceNameOptions = config[this.state.gender].sort().map((item: any) => {
                            return {
                                id: item,
                                name: item
                            };
                        });
                        voiceNameOptions.unshift(this.emptyOption);
                        this.setState({
                            voiceNameOptions
                        });
                    }
                }
            }
        });
    };

    public handleSelectVoiceNameChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            voiceName: event.currentTarget.value,
            voiceNameInvalid: false
        });
    };

    public handleSpeedChange = (event: any) => {
        this.setState({
            speed: parseFloat(event.target.value)
        });
    };

    public handlePitchChange = (event: any) => {
        this.setState({
            pitch: parseFloat(event.target.value)
        });
    };

    public handleAutoPlayChange = (event: any) => {
        this.setState({
            autoPlay: event.target.checked
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (this.state.loading) {
            return;
        }

        let valid = true;

        if (!this.state.text.trim()) {
            this.setState({
                textEmpty: true
            });
            valid = false;
        }

        if (this.state.showLimitWarning) {
            valid = false;
        }

        if (!this.state.gender) {
            this.setState({
                genderInvalid: true
            });
            valid = false;
        }

        if (!this.state.voiceName) {
            this.setState({
                voiceNameInvalid: true
            });
            valid = false;
        }

        if (!valid) {
            return;
        }

        const data = {
            text: this.state.text,
            language_code: this.state.languageCode,
            gender: this.state.gender.toUpperCase(),
            name: this.state.voiceName,
            speaking_rate: this.state.speed,
            pitch: this.state.pitch
        };

        this.setState({
            loading: true,
            result: null,
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getTextToSpeechResult(data).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to get audio."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            const blob = response.data;
            this.setState({
                loading: false,
                result: {
                    blob,
                    src: URL.createObjectURL(blob),
                    type: blob.type
                }
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to get audio."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TEXT_TO_SPEECH);
    };

    public downloadResultAsFile = (e: any) => {
        e.target.blur();

        if (this.state.result) {
            const element = document.createElement("a");
            element.href = this.state.result.src;
            element.download = "result.mp3";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }
    }

    public render() {
        return (
            <ModelContentContainer>
                <Card>
                    <Field>
                        <Control>
                            <SectionName>Text</SectionName>
                            <FieldDescription>Maximum size of input is 5000 characters.</FieldDescription>
                            <TextArea
                                style={(this.state.textEmpty || this.state.showLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                                onChange={this.handleTextChange}
                                onPaste={this.handleTextChange}
                                value={this.state.text}
                                placeholder="Enter text here"
                            />
                            <TextInputWarningsContainer>
                                {
                                    this.state.textEmpty && <TextInputWarning>Please input a text.</TextInputWarning>
                                }
                                {
                                    this.state.showLimitWarning &&
                                    <TextInputWarning>Input size limit has been exceeded. Please decrease the
                                        size.</TextInputWarning>
                                }
                                <CharactersCountLabel>{this.state.charactersCount}&nbsp;{this.state.charactersCount > 1 ? 'characters' : 'character'}</CharactersCountLabel>
                            </TextInputWarningsContainer>
                        </Control>
                    </Field>
                    <Field>
                        <InputLabel>Language:</InputLabel>
                        <SelectInputControl className="select">
                            <SelectField
                                onChange={this.handleSelectLanguageChange}
                                value={this.state.languageCode}
                            >
                                {
                                    this.languageCodeOptions.map((lang) => {
                                        return <SelectOption key={lang.code}
                                                             value={lang.code}>{lang.name}</SelectOption>;
                                    })
                                }
                            </SelectField>
                        </SelectInputControl>
                    </Field>
                    <Field>
                        <InputLabel>Gender:</InputLabel>
                        <SelectInputControl className="select">
                            <SelectField
                                style={this.state.genderInvalid ? INVALID_FIELD_STYLE : undefined}
                                onChange={this.handleSelectGenderChange}
                                value={this.state.gender}
                            >
                                {
                                    this.genderOptions.map((gender) => {
                                        return <SelectOption key={gender.id}
                                                             value={gender.id}>{gender.name}</SelectOption>;
                                    })
                                }
                            </SelectField>
                        </SelectInputControl>
                        {
                            this.state.genderInvalid && <InputWarning>Please select gender.</InputWarning>
                        }
                    </Field>
                    <Field>
                        <InputLabel>Voice Name:</InputLabel>
                        <SelectInputControl className="select">
                            <SelectField
                                style={this.state.voiceNameInvalid ? INVALID_FIELD_STYLE : undefined}
                                onChange={this.handleSelectVoiceNameChange}
                                value={this.state.voiceName}
                            >
                                {
                                    this.state.voiceNameOptions.map((voiceName: any) => {
                                        return <SelectOption key={voiceName.id}
                                                             value={voiceName.id}>{voiceName.name}</SelectOption>;
                                    })
                                }
                            </SelectField>
                        </SelectInputControl>
                        {
                            this.state.voiceNameInvalid && <InputWarning>Please select voice name.</InputWarning>
                        }
                    </Field>
                    <Field>
                        <InputLabel>Speed:</InputLabel>
                        <InputControl>
                            <RangeInput
                                type="range"
                                min="0.25"
                                max="4"
                                step="0.01"
                                onChange={this.handleSpeedChange}
                                value={this.state.speed}
                            />
                        </InputControl>
                        <InputRangeValue>{this.state.speed.toFixed(2)}</InputRangeValue>
                    </Field>
                    <Field>
                        <InputLabel>Pitch:</InputLabel>
                        <InputControl>
                            <RangeInput
                                type="range"
                                min="-20"
                                max="20"
                                step="0.01"
                                onChange={this.handlePitchChange}
                                value={this.state.pitch}
                            />
                        </InputControl>
                        <InputRangeValue>{this.state.pitch.toFixed(2)}</InputRangeValue>
                    </Field>
                    <Field>
                        <InputLabel>Autoplay:</InputLabel>
                        <InputControl>
                            <AutoPlayLabel>
                                <Checkbox
                                    checked={this.state.autoPlay}
                                    onChange={this.handleAutoPlayChange}
                                />&nbsp;&nbsp;{this.state.autoPlay ? 'on' : 'off'}</AutoPlayLabel>
                        </InputControl>
                    </Field>
                    <div>
                        <Button onClick={this.onSubmit}>Submit</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                    </div>
                    {
                        this.state.loading && <div>
                            <Spacer/>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div>
                    }

                    {
                        !this.state.loading && this.state.result ? <div>
                            <Spacer/>
                            <Spacer/>
                            <Spacer/>
                            <SectionName>Result:</SectionName>
                            <InformationCard>
                                <AudioContainer>
                                    <audio
                                        style={this.audioStyle}
                                        controls={true}
                                        autoPlay={this.state.autoPlay}
                                    >
                                        <source
                                            src={this.state.result.src}
                                            type={this.state.result.type}
                                        />
                                    </audio>
                                </AudioContainer>
                                <DownloadButtonContainer>
                                    <Button onClick={this.downloadResultAsFile} title="Download as .mp3 file">
                                        <Icon className="far fa-file-alt" style={{marginRight: '5px'}}/>Download
                                    </Button>
                                </DownloadButtonContainer>
                            </InformationCard>
                        </div> : <></>
                    }
                    {
                        this.state.errorMessage && <ErrorMessage>{this.state.errorMessage}</ErrorMessage>
                    }
                </Card>
            </ModelContentContainer>
        );
    }
}

export default TextToSpeechModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/TextToSpeech/TextToSpeechModel.tsx